/**
 * The app's one analytics entry point.
 *
 * Phase 2 built the beacon, the event contract and the ingest Worker before any
 * gameplay existed, on the reasoning that instrumenting retrospectively always
 * loses the launch-week funnel — the most valuable data the product will ever
 * produce. This is the wiring that finally makes that true.
 *
 * Two rules hold here:
 *
 * 1. **Analytics must never break the game.** Every call goes through `track`
 *    below, which swallows anything the beacon throws. A missing event costs a
 *    row in a chart; an exception costs the user their match.
 * 2. **Never record anything identifying.** The contract enforces this at
 *    ingest — property names like `email` or `ip` are refused outright — but
 *    the first line of defence is not sending them. Team names are the one
 *    piece of free text a user can enter, and they are never transmitted.
 */

import { createBeacon, type Beacon } from '@ipl-sim/analytics/beacon';
import type { EventName } from '@ipl-sim/analytics';
import { heldVersion } from './coefficients';

/**
 * Where events go. Unset means analytics is off, which is the default in
 * development — a dev server has no ingest Worker, and pointing at production
 * from a laptop would poison the funnel with developer sessions.
 */
const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT ?? '';

let beacon: Beacon | null = null;
/**
 * Kept so the coefficient version can be corrected in place. createBeacon reads
 * this object at send time, so updating it relabels every later batch.
 */
let beaconConfig: { coefficientVersion: string } | null = null;

export function initAnalytics(): void {
  if (beacon || !endpoint) return;
  try {
    // Seeded with the version this build expects, then corrected by
    // setCoefficientVersion() once the real set has been fetched -- the server
    // may be serving a newer one, and an event labelled with the wrong version
    // is worse than useless when replaying a disputed result.
    beaconConfig = {
      endpoint,
      appVersion: __APP_VERSION__,
      coefficientVersion: String(heldVersion()),
    } as { coefficientVersion: string };
    beacon = createBeacon(beaconConfig as Parameters<typeof createBeacon>[0]);

    if (beacon.isFirstOpen) {
      // document.referrer is the only acquisition signal available without a
      // third-party tracker. Same-origin referrers say nothing, so drop them.
      const ref = document.referrer;
      const external = ref && new URL(ref).origin !== location.origin ? new URL(ref).hostname : '';
      track('first_open', { referrer: external });
    }
    track('session_start');
  } catch {
    beacon = null;
  }
}

export function track(name: EventName, props?: Record<string, string | number | boolean>): void {
  try {
    beacon?.track(name, props);
  } catch {
    /* analytics must never break the app */
  }
}

/** Called once the fetched coefficient set is known. */
export function setCoefficientVersion(version: number): void {
  if (beaconConfig) beaconConfig.coefficientVersion = String(version);
}

export function flushAnalytics(): void {
  try {
    beacon?.flush();
  } catch {
    /* ignore */
  }
}

/**
 * Time to interactive, reported once. `load_time` is what tells you whether the
 * roster fetch is hurting first-open on a slow Indian 4G connection, which is
 * the single technical number that matters most for this product.
 */
export function trackLoad(cacheHit: boolean): void {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    track('load_time', {
      ttiMs: Math.round(nav ? performance.now() - nav.startTime : performance.now()),
      cacheHit,
    });
  } catch {
    /* ignore */
  }
}

/**
 * Unhandled errors, which double as this project's only error reporting — the
 * plan explicitly budgets zero for a third-party error service and relies on
 * these events instead.
 */
export function trackErrors(): void {
  if (!endpoint) return;
  window.addEventListener('error', (e) => {
    track('js_error', { message: String(e.message).slice(0, 200), source: `${e.filename}:${e.lineno}` });
  });
  window.addEventListener('unhandledrejection', (e) => {
    track('js_error', { message: String(e.reason).slice(0, 200), source: 'promise' });
  });
}
